import * as THREE from "three";
import { BokehPass } from "three/addons/postprocessing/BokehPass.js";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";

import { clamp } from "./utils.js";

/**
 * Cinematic post-processing chain. Bloom shapes the particle energy while a
 * restrained Bokeh pass gives foreground/background sparks optical depth.
 */
export class PostProcessing {
  constructor(renderer, scene, camera) {
    this.composer = new EffectComposer(renderer);
    this.renderPass = new RenderPass(scene, camera);
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(1, 1),
      1.18,
      0.62,
      0.16,
    );
    this.bokehPass = new BokehPass(scene, camera, {
      focus: 8.4,
      aperture: 0.000018,
      maxblur: 0.0016,
    });
    this.depthOfFieldRequested = true;
    this.depthOfFieldAllowedByViewport = true;
    this.outputPass = new OutputPass();

    this.composer.addPass(this.renderPass);
    this.composer.addPass(this.bloomPass);
    this.composer.addPass(this.bokehPass);
    this.composer.addPass(this.outputPass);
  }

  setGlow(value) {
    const glow = clamp(value, 0, 2.5);
    this.bloomPass.strength = 0.24 + glow * 0.72;
    this.bloomPass.radius = clamp(0.42 + glow * 0.1, 0.42, 0.7);
  }

  setFocus(distance) {
    this.bokehPass.uniforms.focus.value = clamp(distance, 4.5, 13);
  }

  setQuality({ depthOfField = true } = {}) {
    this.depthOfFieldRequested = depthOfField;
    this.updateDepthOfFieldState();
  }

  setSize(width, height, pixelRatio) {
    this.composer.setPixelRatio(pixelRatio);
    this.composer.setSize(width, height);
    // The extra depth render is reserved for layouts where it is perceptible.
    this.depthOfFieldAllowedByViewport = width >= 720 && height >= 540;
    this.updateDepthOfFieldState();
  }

  updateDepthOfFieldState() {
    this.bokehPass.enabled =
      this.depthOfFieldRequested && this.depthOfFieldAllowedByViewport;
  }

  render(delta) {
    this.composer.render(delta);
  }

  dispose() {
    this.renderPass.dispose?.();
    this.bloomPass.dispose();
    this.bokehPass.dispose();
    this.outputPass.dispose?.();
    this.composer.dispose();
  }
}
